import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { KeyboardArrowUp } from '@mui/icons-material';
import SearchService from '../../services/SearchService';
import SearchPanel from './SearchPanel';
import ItemsList from './ItemsList';
import SetsList from './SetsList';
import CombinedResultsList from './CombinedResultsList';
import DensityToggle from './DensityToggle';
import HoverPanel from '../utils/HoverPanel';
import SlotTooltip from '../build/SlotTooltip';
import SetTooltip from '../build/SetTooltip';
import Build from '../../core/Build';
import {
    createWeaponDamageScorer,
    weaponSheetFor,
    WEAPON_SORT_DAMAGE,
    WEAPON_SORT_DAMAGE_PER_AP,
} from '../../core/weaponDamage';
import './Search.css';
import {
    PAGE_SIZE,
    SKELETON_COUNT,
    DENSITY_COMPACT,
    DENSITY_SCOPE_ENCYCLOPEDIA,
    readSearchDensity,
    writeSearchDensity,
} from '../../core/searchConfig';
import { calculateTooltipPosition } from '../../utils/tooltipUtils';
import { usePointerHover } from '../../hooks/usePointerHover';
import { useEquipItem } from '../../hooks/useEquipItem';
import { useGeneratorContext } from '../../contexts/GeneratorStateContext';

const TXT_RESULTS = { fr: 'résultats', en: 'results', de: 'Ergebnisse', es: 'resultados', pt: 'resultados' };
const TXT_SETS_FOUND = { fr: 'panoplies trouvées', en: 'sets found', de: 'gefundene Sets', es: 'sets encontrados', pt: 'conjuntos encontrados' };
const TXT_ITEMS_FOUND = { fr: 'objets trouvés', en: 'items found', de: 'gefundene Gegenstände', es: 'objetos encontrados', pt: 'itens encontrados' };

const TXT_BACK_TO_TOP = { fr: 'Revenir en haut', en: 'Back to top', de: 'Nach oben', es: 'Volver arriba', pt: 'Voltar ao topo' };

// How far down you have to be before the back-to-top button appears — about the
// height of the search panel plus the quick filters, so it shows up once the
// controls have actually scrolled out of reach and not a moment before.
const BACK_TO_TOP_SCROLL = 600;

const DEFAULT_LVL = 200;
const TXT_SEARCH_PENDING = { fr: 'Recherche en cours...', en: 'Searching...', de: 'Suche läuft...', es: 'Buscando...', pt: 'Buscando...' };

// Same discriminator the result lists use: backend `search_result_type` first,
// structure only as a fallback.
const isSetResult = (result) => {
    if (result?.search_result_type) return result.search_result_type === 'set';
    return Boolean(result?.bonuses || (Array.isArray(result?.items) && result?.ankama_id === undefined));
};

const getResultId = (result) => result?.ankama_id ?? result?.id ?? null;

/** Cleared panel state. Named so the places that reset each one agree. */
const NO_TOOLTIP = { item: null, anchor: null, interactive: false, onOpenItem: null, onOpenSet: null };
const NO_SET_PREVIEW = { set: null, anchor: null, interactive: false, onOpenSet: null, onOpenItem: null };

/**
 * Shortcut back to the search panel, for wherever the panel scrolls away with
 * the results: on a phone, and at every width in the slot overlay, which has no
 * sticky panel to scroll back to. Hidden by CSS everywhere else.
 */
const BackToTopButton = ({ lang, alwaysShown = false }) => {
    const buttonRef = useRef(null);
    const scrollerRef = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const node = buttonRef.current;
        if (!node) return undefined;

        // The results scroll inside whatever container Search was dropped into —
        // .main-content on the encyclopedia page, .overlay-content in the slot
        // picker — and guessing it by walking up picks the wrong one (several
        // ancestors are overflow: auto without ever being a scrollport). So let
        // the scroll event name it: scroll doesn't bubble but is capturable, and
        // the container we want is the one that both scrolls and holds us.
        const onScroll = (event) => {
            const scroller = event.target === document ? document.scrollingElement : event.target;
            if (!scroller?.contains?.(node)) return;
            // Keep the innermost once found, so an outer container scrolling too
            // cannot steal the reference.
            const known = scrollerRef.current;
            if (known && known !== scroller && scroller.contains(known)) return;
            scrollerRef.current = scroller;
            // One comparison, and React bails out when the boolean is unchanged,
            // so this needs no throttle of its own.
            setVisible(scroller.scrollTop > BACK_TO_TOP_SCROLL);
        };

        document.addEventListener('scroll', onScroll, { capture: true, passive: true });
        return () => document.removeEventListener('scroll', onScroll, { capture: true });
    }, []);

    const label = TXT_BACK_TO_TOP[lang] || TXT_BACK_TO_TOP.en;

    return (
        <button
            ref={buttonRef}
            type="button"
            className={`search-back-to-top alt${alwaysShown ? ' search-back-to-top--always' : ''}${visible ? ' visible' : ''}`}
            onClick={() => scrollerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label={label}
            title={label}
            aria-hidden={!visible}
            tabIndex={visible ? 0 : -1}
        >
            <KeyboardArrowUp />
        </button>
    );
};

const Search = ({ lang = 'fr', initialFilters: externalInitialFilters, onSelectItem, onCardClick, scrollContainerSelector, initialSearchMode, disableTypeFilters = false, preferredSlotId = null, enableStatsQuickPanel = false, damageSortBuild = null, equipLabel = null, equipIcon = null, autoFocus = false, pinnedItem = null, pinnedWeaponMaging = null, pinnedItemMaging = null, onPinnedClick = null, inOverlay = false, densityScope = DENSITY_SCOPE_ENCYCLOPEDIA }) => {
    const hasPointerHover = usePointerHover();
    const { equipItem, equipSet } = useEquipItem(lang);
    const [results, setResults] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    // SearchPanel fires the first search from an effect, so there is a gap on mount
    // where nothing is in flight yet. Without this the grid would sit blank instead
    // of showing skeletons, and the header would claim "0 found" before any search ran.
    const [hasSearched, setHasSearched] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [, setCurrentPage] = useState(1);
    const defaultSearchMode = initialSearchMode || { searchItems: true, searchSets: false };
    const [searchMode, setSearchMode] = useState(defaultSearchMode);
    const currentFilters = useRef({});
    // Not a filter, and not something the API can answer, so it travels beside
    // the filters rather than inside them — see SearchService.searchItems.
    const sortValueRef = useRef(null);
    // Mirrors the weapon section's damage setting for the cards, which read it to
    // decide between computed damage and the plain dice. Seeded to that control's
    // own default so the pinned card, which renders before the first search comes
    // back, does not start on dice and switch under the eye.
    const [showWeaponDamage, setShowWeaponDamage] = useState(() => Boolean(damageSortBuild));
    // The damage figures and orderings are for the side the build fights on.
    const position = useGeneratorContext()?.genConfig?.position;

    // One scorer per build *and* ordering, so the whole weapon list is priced
    // once per ordering and every later keystroke reads the cache. Both are
    // built up front because a Map fills lazily — the one the panel is not
    // asking for costs nothing until it is asked for. The weaponless base sheet
    // both of them price against is derived once and shared, on the build.
    const damageScorers = useMemo(() => {
        if (!damageSortBuild) return null;
        return {
            [WEAPON_SORT_DAMAGE]: createWeaponDamageScorer({ build: damageSortBuild, sort: WEAPON_SORT_DAMAGE, position }),
            [WEAPON_SORT_DAMAGE_PER_AP]: createWeaponDamageScorer({ build: damageSortBuild, sort: WEAPON_SORT_DAMAGE_PER_AP, position }),
        };
    }, [damageSortBuild, position]);

    const stateRef = useRef();
    stateRef.current = { loading, hasMore };

    // Detailed cards or compact tiles. A display preference, not a filter, so it
    // never re-runs the search — and it outlives the session, per surface.
    const [density, setDensity] = useState(() => readSearchDensity(densityScope));
    const dense = density === DENSITY_COMPACT;
    const handleDensityChange = useCallback((next) => {
        setDensity(next);
        writeSearchDensity(densityScope, next);
    }, [densityScope]);

    // Tooltip state for item thumbnails in sets, and for the compact tiles that
    // have nothing but art and a name to go on.
    const [tooltip, setTooltip] = useState(NO_TOOLTIP);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    // The rendered panel, so a tile's mouseleave can tell "the pointer went into
    // the panel" from "the pointer went away" — see the handlers below.
    const tooltipElRef = useRef(null);
    const setPreviewElRef = useRef(null);
    const [setPreview, setSetPreview] = useState(NO_SET_PREVIEW);
    const [setPreviewPos, setSetPreviewPos] = useState({ x: 0, y: 0 });

    // The build the panel is read against. The weapon slot's search hands one
    // over; everywhere else (the encyclopedia, the exclusion picker) there is no
    // build to speak of, and a bare level is enough to render an item's own
    // numbers.
    const tooltipBuild = useMemo(
        () => damageSortBuild || new Build({ lvl: DEFAULT_LVL }),
        [damageSortBuild],
    );

    // In compact mode the tile carries no stats, so the panel is the card — and
    // a weapon card prices the weapon against the build. Same scorer input the
    // detailed card uses, under the same setting.
    const tooltipWeaponStats = useMemo(() => {
        if (!damageSortBuild || !showWeaponDamage || !tooltip.item?.weapon) return null;
        return weaponSheetFor(damageSortBuild, tooltip.item, position);
    }, [damageSortBuild, showWeaponDamage, tooltip.item, position]);

    const initialSearchFilters = {
        limit: PAGE_SIZE,
        offset: 0,
        item_type_ids: [],
        ...(externalInitialFilters || {})
    };

    const handleSelectResult = useCallback((selected) => {
        if (onSelectItem) {
            onSelectItem(selected);
            return;
        }
        if (!selected) return;
        const isSet = selected.search_result_type === 'set'
            || (Array.isArray(selected.items) && selected.items.length > 0 && !selected.item_type_id);
        if (isSet) {
            equipSet(selected, preferredSlotId);
        } else {
            equipItem(selected, preferredSlotId);
        }
    }, [onSelectItem, equipItem, equipSet, preferredSlotId]);

    const fetchUnified = useCallback(async (filters, page, mode) => {
        setLoading(true);
        try {
            const offset = (page - 1) * PAGE_SIZE;
            const searchFilters = { ...filters, limit: PAGE_SIZE, offset };
            const { results: fetchedResults, total } = await SearchService.searchUnified(
                searchFilters, lang, { sortValue: sortValueRef.current },
            );
            setResults(prev => page === 1 ? fetchedResults : [...prev, ...fetchedResults]);
            setTotalCount(total);
            setHasMore(fetchedResults.length === PAGE_SIZE && (offset + fetchedResults.length) < total);
        } catch (error) {
            console.error("Error during unified search:", error);
        } finally {
            setLoading(false);
            setHasSearched(true);
        }
    }, [lang]);

    const fetchItems = useCallback(async (filters, page) => {
        setLoading(true);
        try {
            const offset = (page - 1) * PAGE_SIZE;
            const searchFilters = { ...filters, limit: PAGE_SIZE, offset };
            const { results: fetchedResults, total } = await SearchService.searchItems(
                searchFilters, lang, { sortValue: sortValueRef.current },
            );
            setResults(prev => page === 1 ? fetchedResults : [...prev, ...fetchedResults]);
            setTotalCount(total);
            setHasMore(fetchedResults.length === PAGE_SIZE && (offset + fetchedResults.length) < total);
        } catch (error) {
            console.error("Error during item search:", error);
        } finally {
            setLoading(false);
            setHasSearched(true);
        }
    }, [lang]);

    const fetchSets = useCallback(async (filters, page) => {
        setLoading(true);
        try {
            const offset = (page - 1) * PAGE_SIZE;
            const searchFilters = { ...filters, limit: PAGE_SIZE, offset };
            const { results: fetchedResults, total } = await SearchService.searchSets(searchFilters, lang);
            setResults(prev => page === 1 ? fetchedResults : [...prev, ...fetchedResults]);
            setTotalCount(total);
            setHasMore(fetchedResults.length === PAGE_SIZE && (offset + fetchedResults.length) < total);
        } catch (error) {
            console.error("Error during set search:", error);
        } finally {
            setLoading(false);
            setHasSearched(true);
        }
    }, [lang]);

    const handleSearch = useCallback((filters, mode = { searchItems: true, searchSets: false }) => {
        // The weapon section's two settings, neither of them something to search
        // on: the sort resolves here into the scorer the local search orders by
        // (null for level, which the catalog's own field already answers), and
        // the damage setting into what the cards compute. Neither reaches the
        // request.
        const { weapon_damage: withWeaponDamage, weapon_sort: weaponSort, ...searchFilters } = filters;
        sortValueRef.current = damageScorers?.[weaponSort] || null;
        setShowWeaponDamage(Boolean(withWeaponDamage) && Boolean(damageScorers));
        currentFilters.current = searchFilters;
        setSearchMode(mode);
        setCurrentPage(1);
        setResults([]);
        setHasMore(true);

        const isBothActive = mode.searchItems && mode.searchSets;
        const isItemsOnly = mode.searchItems && !mode.searchSets;
        const isSetsOnly = !mode.searchItems && mode.searchSets;

        if (isBothActive) {
            fetchUnified(searchFilters, 1, mode);
        } else if (isItemsOnly) {
            fetchItems(searchFilters, 1);
        } else if (isSetsOnly) {
            fetchSets(searchFilters, 1);
        }
    }, [fetchUnified, fetchItems, fetchSets, damageScorers]);

    // SearchPanel triggers the initial search on mount via its own useEffect.
    // Do NOT run handleSearch here to avoid double API calls (no-filters then full-filters).

    const loadMoreResults = useCallback(() => {
        if (!stateRef.current.loading && stateRef.current.hasMore) {
            setCurrentPage(prevPage => {
                const nextPage = prevPage + 1;
                const isBothActive = searchMode.searchItems && searchMode.searchSets;
                const isItemsOnly = searchMode.searchItems && !searchMode.searchSets;
                const isSetsOnly = !searchMode.searchItems && searchMode.searchSets;

                if (isBothActive) {
                    fetchUnified(currentFilters.current, nextPage, searchMode);
                } else if (isItemsOnly) {
                    fetchItems(currentFilters.current, nextPage);
                } else if (isSetsOnly) {
                    fetchSets(currentFilters.current, nextPage);
                }
                return nextPage;
            });
        }
    }, [fetchUnified, fetchItems, fetchSets, searchMode]);

    // Item panel handlers. A compact tile's panel is *interactive* (the build
    // slots' hoverable tooltip, same component): it accepts the pointer, so the
    // tile it belongs to has to keep it up when the pointer leaves towards it,
    // and the panel has to keep itself up when the pointer leaves back towards
    // the tile. Both directions are read off `relatedTarget` — the element the
    // pointer is entering — which is exact and needs no grace timer; the visible
    // gap between the two is spanned by the panel's own hit-strip
    // (.hover-panel--interactive in HoverPanel.css), so nothing else sits
    // between them on the way. A set card's piece thumbnails sit in a row and
    // get the plain, click-through panel instead — theirs pass no options.
    const handleTooltipShow = useCallback((item, event, options = {}) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setTooltip({
            item,
            anchor: event.currentTarget,
            interactive: !!options.interactive,
            onOpenItem: options.onOpenItem || null,
            onOpenSet: options.onOpenSet || null,
        });
        setTooltipPos(calculateTooltipPosition(rect));
    }, []);

    const handleTooltipHide = useCallback((event) => {
        // Crossing from the tile into its own panel is not leaving it.
        const entering = event?.relatedTarget;
        if (entering instanceof Node && tooltipElRef.current?.contains(entering)) return;
        setTooltip(NO_TOOLTIP);
    }, []);

    // The mirror: leaving the panel back towards its own tile keeps it up,
    // because the tile is about to re-assert it anyway and the flicker in
    // between is what the user would see.
    const handleTooltipLeave = useCallback((event) => {
        const entering = event?.relatedTarget;
        if (entering instanceof Node && tooltip.anchor?.contains(entering)) return;
        setTooltip(NO_TOOLTIP);
    }, [tooltip.anchor]);

    // The set panel is the item panel's twin, down to the handlers: a compact
    // set tile's panel is hoverable too, so the same two relatedTarget checks
    // decide whether the pointer left or merely moved between the pair.
    const handleSetTooltipShow = useCallback((set, event, options = {}) => {
        const rect = event.currentTarget.getBoundingClientRect();
        setSetPreview({
            set,
            anchor: event.currentTarget,
            interactive: !!options.interactive,
            onOpenSet: options.onOpenSet || null,
            onOpenItem: options.onOpenItem || null,
        });
        setSetPreviewPos(calculateTooltipPosition(rect));
    }, []);

    const handleSetTooltipHide = useCallback((event) => {
        const entering = event?.relatedTarget;
        if (entering instanceof Node && setPreviewElRef.current?.contains(entering)) return;
        setSetPreview(NO_SET_PREVIEW);
    }, []);

    const handleSetTooltipLeave = useCallback((event) => {
        const entering = event?.relatedTarget;
        if (entering instanceof Node && setPreview.anchor?.contains(entering)) return;
        setSetPreview(NO_SET_PREVIEW);
    }, [setPreview.anchor]);

    useEffect(() => {
        if (hasPointerHover) return;
        setTooltip(NO_TOOLTIP);
        setSetPreview(NO_SET_PREVIEW);
    }, [hasPointerHover]);

    // A card the results replaced under the pointer never fires mouseleave, so
    // its panel would hang around anchored to a node that is gone.
    useEffect(() => {
        if (!tooltip.item) return;
        if (tooltip.anchor && !tooltip.anchor.isConnected) {
            setTooltip(NO_TOOLTIP);
        }
    }, [tooltip]);

    useEffect(() => {
        if (!setPreview.set) return;
        if (setPreview.anchor && !setPreview.anchor.isConnected) {
            setSetPreview(NO_SET_PREVIEW);
        }
    }, [setPreview]);

    const isBothActive = searchMode.searchItems && searchMode.searchSets;
    const resultsText = isBothActive
        ? (TXT_RESULTS[lang] || TXT_RESULTS.en)
        : searchMode.searchSets
            ? (TXT_SETS_FOUND[lang] || TXT_SETS_FOUND.en)
            : (TXT_ITEMS_FOUND[lang] || TXT_ITEMS_FOUND.en);
    // Awaiting results covers both "request in flight" and "first one not sent yet".
    const isAwaitingResults = loading || !hasSearched;
    const isPendingSearch = isAwaitingResults && results.length === 0;
    const skeletonCount = isAwaitingResults ? SKELETON_COUNT : 0;
    // The pinned card already stands for that item — drop the duplicate the
    // search returns whenever the equipped item matches the current filters.
    // The header keeps the server total, which counted it once.
    const pinnedItemId = pinnedItem ? getResultId(pinnedItem) : null;
    const visibleResults = useMemo(() => {
        if (pinnedItemId == null) return results;
        return results.filter(result => isSetResult(result) || getResultId(result) !== pinnedItemId);
    }, [results, pinnedItemId]);
    const resultsHeaderText = isPendingSearch
        ? (TXT_SEARCH_PENDING[lang] || TXT_SEARCH_PENDING.en)
        : `${totalCount} ${resultsText}`;

    const renderResults = () => {
        if (isBothActive) {
            return (
                <CombinedResultsList
                    results={visibleResults}
                    lang={lang}
                    dense={dense}
                    skeletonCount={skeletonCount}
                    onLastResultVisible={loadMoreResults}
                    onItemClick={handleSelectResult}
                    onSetClick={handleSelectResult}
                    onTooltipShow={hasPointerHover ? handleTooltipShow : undefined}
                    onTooltipHide={hasPointerHover ? handleTooltipHide : undefined}
                    onSetTooltipShow={hasPointerHover && dense ? handleSetTooltipShow : undefined}
                    onSetTooltipHide={hasPointerHover && dense ? handleSetTooltipHide : undefined}
                    preferredSlotId={preferredSlotId}
                    showWeaponDamage={showWeaponDamage}
                    pinnedItem={pinnedItem}
                    pinnedWeaponMaging={pinnedWeaponMaging}
                    pinnedItemMaging={pinnedItemMaging}
                    onPinnedClick={onPinnedClick}
                />
            );
        } else if (searchMode.searchSets) {
            return (
                <SetsList
                    sets={visibleResults}
                    lang={lang}
                    dense={dense}
                    skeletonCount={skeletonCount}
                    onLastSetVisible={loadMoreResults}
                    onSetClick={handleSelectResult}
                    onItemClick={handleSelectResult}
                    onTooltipShow={hasPointerHover ? handleTooltipShow : undefined}
                    onTooltipHide={hasPointerHover ? handleTooltipHide : undefined}
                    onSetTooltipShow={hasPointerHover && dense ? handleSetTooltipShow : undefined}
                    onSetTooltipHide={hasPointerHover && dense ? handleSetTooltipHide : undefined}
                    preferredSlotId={preferredSlotId}
                    showWeaponDamage={showWeaponDamage}
                    pinnedItem={pinnedItem}
                    pinnedWeaponMaging={pinnedWeaponMaging}
                    pinnedItemMaging={pinnedItemMaging}
                    onPinnedClick={onPinnedClick}
                />
            );
        } else {
            return (
                <ItemsList
                    items={visibleResults}
                    lang={lang}
                    dense={dense}
                    skeletonCount={skeletonCount}
                    onLastItemVisible={loadMoreResults}
                    onItemClick={handleSelectResult}
                    onCardClick={onCardClick}
                    onTooltipShow={hasPointerHover && dense ? handleTooltipShow : undefined}
                    onTooltipHide={hasPointerHover && dense ? handleTooltipHide : undefined}
                    preferredSlotId={preferredSlotId}
                    equipLabel={equipLabel}
                    equipIcon={equipIcon}
                    showWeaponDamage={showWeaponDamage}
                    pinnedItem={pinnedItem}
                    pinnedWeaponMaging={pinnedWeaponMaging}
                    pinnedItemMaging={pinnedItemMaging}
                    onPinnedClick={onPinnedClick}
                />
            );
        }
    };

    // The pinned tile's panel shows the slot's forgemaged item, like the card.
    const isPinnedTooltip = pinnedItemId != null && getResultId(tooltip.item) === pinnedItemId;

    return (
        <div className="search-container column">
            <SearchPanel
                onSearch={handleSearch}
                lang={lang}
                initialFilters={initialSearchFilters}
                scrollContainerSelector={scrollContainerSelector}
                initialSearchMode={defaultSearchMode}
                disableTypeFilters={disableTypeFilters}
                enableStatsQuickPanel={enableStatsQuickPanel}
                enableWeaponOptions={!!damageScorers}
                autoFocus={autoFocus}
                bare={inOverlay}
            />
            <div className="search-results">
                <div className="results-header">
                    <h4>{resultsHeaderText}</h4>
                    <DensityToggle lang={lang} density={density} onChange={handleDensityChange} />
                </div>
                {renderResults()}
                {/* Item panel: set thumbnails in detailed mode, whole cards in compact.
                    Set panel: compact set tiles only. HoverPanel owns the placement
                    and, for the hoverable pair, the strip that spans the gap to the
                    tile — so it is the box `relatedTarget` is tested against. */}
                {tooltip.item && (
                    <HoverPanel
                        ref={tooltipElRef}
                        pos={tooltipPos}
                        interactive={tooltip.interactive}
                        onMouseLeave={tooltip.interactive ? handleTooltipLeave : undefined}
                    >
                        <SlotTooltip
                            item={tooltip.item}
                            build={tooltipBuild}
                            weaponStats={tooltipWeaponStats}
                            lang={lang}
                            interactive={tooltip.interactive}
                            onOpenItem={tooltip.onOpenItem}
                            onOpenSet={tooltip.onOpenSet}
                            onDismiss={handleTooltipHide}
                            itemMaging={isPinnedTooltip ? pinnedItemMaging : null}
                        />
                    </HoverPanel>
                )}
                {setPreview.set && (
                    <HoverPanel
                        ref={setPreviewElRef}
                        pos={setPreviewPos}
                        interactive={setPreview.interactive}
                        onMouseLeave={setPreview.interactive ? handleSetTooltipLeave : undefined}
                    >
                        <SetTooltip
                            set={setPreview.set}
                            lang={lang}
                            interactive={setPreview.interactive}
                            onOpenSet={setPreview.onOpenSet}
                            onOpenItem={setPreview.onOpenItem}
                            onDismiss={handleSetTooltipHide}
                        />
                    </HoverPanel>
                )}
            </div>
            <BackToTopButton lang={lang} alwaysShown={inOverlay} />
        </div>
    );
};

export default Search;
